import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Alert,
} from "@mui/material";
import { DataGridPro, GridActionsCellItem } from "@mui/x-data-grid-pro";
import { Visibility, Delete, Check, Close } from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import useSharedLink from "../../SharedLink";
import axiosInstance from "../../api/axiosInstance";

// Define the RespondDialog component
const RespondDialog = ({ open, onClose, email, subject, sharedLink }) => {
  const [message, setMessage] = useState("");

  const handleSend = () => {
    const emailPayload = {
      to: [email],
      subject: subject,
      body: message,
    };

    axiosInstance
      .post(`${sharedLink}/api/send-email`, emailPayload, {
        withCredentials: true,
      })
      .then((response) => {
        //console.log("Email sent successfully:", response.data);
        onClose();
      })
      .catch((error) => {
        console.error("Error sending email:", error);
      });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Respond</DialogTitle>
      <DialogContent>
        <TextField
          label="Email"
          value={email}
          fullWidth
          margin="dense"
          variant="outlined"
          disabled
        />
        <TextField
          label="Subject"
          value={subject}
          fullWidth
          margin="dense"
          variant="outlined"
          disabled
        />
        <TextField
          label="Message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          fullWidth
          margin="dense"
          variant="outlined"
          multiline
          rows={4}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSend} color="primary">
          Send
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// Define the ConfirmationDialog component
const ConfirmationDialog = ({ open, onClose, onConfirm, message }) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>Confirmation</DialogTitle>
    <DialogContent>
      <Typography>{message}</Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onConfirm} color="primary">
        Yes
      </Button>
      <Button onClick={onClose} color="secondary">
        No
      </Button>
    </DialogActions>
  </Dialog>
);

const FeedbackPage = () => {
  const sharedLink = useSharedLink();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [feedbackDetails, setFeedbackDetails] = useState({
    date: "",
    email: "",
    id: null,
    message: "",
    name: "",
    approved: false,
  });
  const [showInactive, setShowInactive] = useState(true);
  const [respondOpen, setRespondOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogAction, setConfirmDialogAction] = useState(null);

  const navigate = useNavigate();

  const fetchAllFeedback = useCallback(() => {
    axiosInstance
      .post(
        `${sharedLink}/api/abanotes/getallfeedback`,
        {},
        { withCredentials: true }
      )
      .then((response) => {
        const filteredFeedbacks = response.data
          .filter((feedback) => feedback.approved === !showInactive)
          .sort((a, b) => new Date(b.feedbackTime) - new Date(a.feedbackTime)); // Sort by feedbackTime in descending order
        setRows(filteredFeedbacks);
      })
      .catch((error) => {
        console.error("Error fetching feedbacks:", error);
      });
  }, [sharedLink, showInactive]);

  useEffect(() => {
    fetchAllFeedback();
  }, [fetchAllFeedback, showInactive]);

  const handleView = (id, email) => {
    const payload = id ? { id } : { email };

    axiosInstance
      .post(`${sharedLink}/api/abanotes/getfeedback`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      })
      .then((response) => {
        const feedback = response.data[0];
        const { feedbackTime, email, id, message, name, approved } = feedback;
        const formattedDate = new Date(feedbackTime)
          .toISOString()
          .split("T")[0];

        setFeedbackDetails({
          date: formattedDate,
          email,
          id,
          message,
          name,
          approved,
        });
        setOpen(true);
      })
      .catch((error) => {
        console.error("Error fetching feedback details:", error);
      });
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this feedback?")) {
      axiosInstance
        .post(
          `${sharedLink}/api/abanotes/deactivatefeedbackus`,
          { id: id },
          {
            headers: {
              "Content-Type": "application/json",
            },
            withCredentials: true,
          }
        )
        .then((response) => {
          setRows((prevRows) => prevRows.filter((row) => row.id !== id));
          setSnackbarMessage("Feedback deactivated successfully");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        })
        .catch((error) => {
          console.error("Error deactivating feedback:", error);
          setSnackbarMessage("Failed to deactivate feedback");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        });
    }
  };

  const handleApprove = (id) => {
    setConfirmDialogAction(() => () => {
      axiosInstance
        .post(
          `${sharedLink}/api/abanotes/approvefeedback`,
          { id: id },
          {
            headers: {
              "Content-Type": "application/json",
            },
            withCredentials: true,
          }
        )
        .then((response) => {
          // Update the row to reflect the approval
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id === id ? { ...row, approved: true } : row
            )
          );
          setSnackbarMessage("Feedback approved successfully");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        })
        .catch((error) => {
          console.error("Error approving feedback:", error);
          setSnackbarMessage("Failed to approve feedback");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        });
    });
    setConfirmDialogOpen(true);
  };

  const handleDisapprove = (id) => {
    setConfirmDialogAction(() => () => {
      axiosInstance
        .post(
          `${sharedLink}/api/abanotes/disapprovefeedback`,
          { id: id },
          {
            headers: {
              "Content-Type": "application/json",
            },
            withCredentials: true,
          }
        )
        .then((response) => {
          // Update the row to reflect the disapproval
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id === id ? { ...row, approved: false } : row
            )
          );
          setSnackbarMessage("Feedback disapproved successfully");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        })
        .catch((error) => {
          console.error("Error disapproving feedback:", error);
          setSnackbarMessage("Failed to disapprove feedback");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        });
    });
    setConfirmDialogOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFeedbackDetails({});
  };

  const handleRespond = () => {
    setRespondOpen(true);
  };

  const handleRespondClose = () => {
    setRespondOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false);
  };

  const handleConfirmDialogConfirm = () => {
    if (confirmDialogAction) {
      confirmDialogAction();
    }
    setConfirmDialogOpen(false);
  };

  const columns = [
    { field: "name", headerName: "Name", width: 200 },
    { field: "email", headerName: "Email", width: 200 },
    {
      field: "feedbackTime",
      headerName: "Date",
      width: 150,
      valueGetter: (params) => {
        const date = new Date(params.value);
        return isNaN(date.getTime())
          ? "Invalid Date"
          : date.toISOString().split("T")[0];
      },
    },
    { field: "message", headerName: "Message", width: 400 },
    { field: "approved", headerName: "Approved", width: 200 },
    {
      field: "actions",
      headerName: "Actions",
      type: "actions",
      width: 100,
      getActions: (params) => {
        const actions = [
          <GridActionsCellItem
            key={`view-${params.id}`}
            icon={<Visibility />}
            label="View"
            onClick={() => handleView(params.id, params.row.email)}
          />,
        ];

        if (params.row.approved) {
          actions.push(
            <GridActionsCellItem
              key={`disapprove-${params.id}`}
              icon={<Close />}
              label="Disapprove"
              onClick={() => handleDisapprove(params.id)}
            />
          );
        } else {
          actions.push(
            <GridActionsCellItem
              key={`approve-${params.id}`}
              icon={<Check />}
              label="Approve"
              onClick={() => handleApprove(params.id)}
            />
          );
        }

        return actions;
      },
    },
  ];

  return (
    <Box sx={{ width: "80%", mt: 20, ml: "auto", mr: "auto", backdropFilter: "blur(10px)"  }}>
      <Typography
        variant="h4"
        sx={{ mb: 10, textAlign: "center", fontWeight: "bold", fontSize: 35 }}
      >
        All Feedbacks
      </Typography>
      <Box sx={{ height: "calc(90vh - 260px)", width: "100%" }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              name="showInactive"
              color="primary"
            />
          }
          label="Show disapproved feedback"
        />
        <DataGridPro
          rows={rows}
          columns={columns}
          pageSize={5}
          getRowId={(row) => row.id}
          className="animated-input"
          sx={{
            boxShadow: 5,
            border: "none",
            "& .MuiDataGrid-cell:hover": {
              color: "primary.main",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "white !important",
              fontWeight: "bold !important",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#e9a132 !important",
              borderBottom: "white !important",
              alignContent: "center",
              alignItems: "center",
              display: "flex",
              height: "100%",
            },
            "& .MuiDataGrid-columnSeparator": {
              visibility: "hidden",
            },
            "& .MuiDataGrid-toolbarContainer": {
              color: "white !important",
              justifyContent: "space-between",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              color: "white !important",
              fontWeight: "bold !important",
              backgroundColor: "#e9a132 !important",
            },
            "& .MuiTablePagination-root": {
              color: "white !important",
            },
            "& .MuiIconButton-root": {
              color: "black !important",
            },
            "& .MuiDataGrid-selectedRowCount": {
              color: "white !important",
            },
          }}
        />
      </Box>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Feedback Details</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={feedbackDetails.name || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            disabled
          />
          <TextField
            label="Email"
            value={feedbackDetails.email || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            disabled
          />
          <TextField
            label="Message"
            value={feedbackDetails.message || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            multiline
            rows={4}
            disabled
          />
          <TextField
            label="Date"
            value={feedbackDetails.date || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            disabled
          />
        </DialogContent>
        <DialogActions>
          {!feedbackDetails.approved ? (
            <Button
              onClick={() => handleApprove(feedbackDetails.id)}
              color="primary"
            >
              Approve
            </Button>
          ) : (
            <Button
              onClick={() => handleDisapprove(feedbackDetails.id)}
              color="secondary"
            >
              Disapprove
            </Button>
          )}
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
      <RespondDialog
        open={respondOpen}
        onClose={handleRespondClose}
        email={feedbackDetails.email}
        subject="Response to your feedback"
        sharedLink={sharedLink}
      />
      <ConfirmationDialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        onConfirm={handleConfirmDialogConfirm}
        message="Are you sure you want to proceed with this action?"
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FeedbackPage;